import { OneDataCollection, useDataCollectionSource } from '@factorialco/f0-react/dist/experimental'
import { JobCatalogEnumsJobCatalogTreeNodeTypesEnum } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  Hierarchy,
  PreviewRow,
  TreeNode,
  Workplace,
} from 'modules/cost_centers/components/CostCentersStructure/types'

import { buildPreviewRows } from './usePreviewData'

export const PreviewCostCenters = ({
  treeArray,
  jobStructure,
  expandWorkplace,
  workplaces,
}: {
  treeArray: TreeNode[]
  jobStructure?: JobCatalogEnumsJobCatalogTreeNodeTypesEnum
  expandWorkplace: boolean
  workplaces: Workplace[]
}) => {
  const previewRows = buildPreviewRows({ treeArray, jobStructure, expandWorkplace, workplaces })

  const jobStructureIndex = jobStructure ? Hierarchy.indexOf(jobStructure) : -1

  const shouldShowJobCatalogColumns = {
    family: jobStructureIndex >= 0,
    function: jobStructureIndex >= 1,
    role: jobStructureIndex >= 2,
  }

  const source = useDataCollectionSource<PreviewRow>({
    dataAdapter: {
      fetchData: () => {
        return {
          pagesCount: 1,
          currentPage: 1,
          records: previewRows,
          type: 'pages' as const,
          total: previewRows.length,
          perPage: previewRows.length,
        }
      },
    },
    totalItemSummary: (totalItems) =>
      i18n.tp('finance.cost_centers_structure.preview.table.count', { count: totalItems }),
  })

  const columns = [
    {
      id: 'name',
      label: i18n.t('finance.cost_centers_structure.preview.table.name'),
      width: 400,
      render: (row: PreviewRow) => ({
        type: 'text' as const,
        value: row.name,
      }),
    },
    {
      id: 'code',
      label: i18n.t('finance.cost_centers_structure.preview.table.code'),
      render: () => ({
        type: 'text' as const,
        value: '-',
      }),
    },
    ...(shouldShowJobCatalogColumns.family
      ? [
          {
            id: 'family',
            label: i18n.t('finance.cost_centers_list.headers.job_catalog.family'),
            render: (row: PreviewRow) => ({
              type: 'text' as const,
              value: row.familyName || '-',
            }),
          },
        ]
      : []),
    ...(shouldShowJobCatalogColumns.function
      ? [
          {
            id: 'function',
            label: i18n.t('finance.cost_centers_list.headers.job_catalog.function'),
            render: (row: PreviewRow) => ({
              type: 'text' as const,
              value: row.functionName || '-',
            }),
          },
        ]
      : []),
    ...(shouldShowJobCatalogColumns.role
      ? [
          {
            id: 'role',
            label: i18n.t('finance.cost_centers_list.headers.job_catalog.role'),
            render: (row: PreviewRow) => ({
              type: 'text' as const,
              value: row.roleName || '-',
            }),
          },
        ]
      : []),
    ...(expandWorkplace
      ? [
          {
            id: 'workplace',
            label: i18n.t('finance.cost_centers.assignments.structured.workplace'),
            render: (row: PreviewRow) => ({
              type: 'text' as const,
              value: row.workplaceName || '-',
            }),
          },
        ]
      : []),
    {
      id: 'legal_entity',
      label: i18n.t('finance.cost_centers_structure.preview.table.legal_entity'),
      render: () => ({
        type: 'text' as const,
        value: i18n.t('finance.cost_centers_structure.preview.table.all_legal_entities'),
      }),
    },
    {
      id: 'status',
      label: i18n.t('finance.cost_centers_structure.preview.table.status'),
      width: 200,
      render: () => ({
        type: 'status' as const,
        value: {
          status: 'positive',
          label: i18n.t('finance.cost_centers_structure.preview.table.active'),
        },
      }),
    },
  ]

  return (
    <OneDataCollection
      fullHeight
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns,
          },
        },
      ]}
    />
  )
}
